import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FaUserCircle, FaEye } from 'react-icons/fa';
import { 
  X, 
  CheckCircle, 
  Loader, 
  Plus, 
  FileText, 
  Newspaper, 
  Link as LinkIcon, 
  ChevronDown, 
  ChevronUp, 
  Calendar,
  AlertCircle,
  Info,
  Save,
  Trash2,
  Target
} from 'lucide-react';
import debounce from 'lodash/debounce';
import { useSearchParams, useNavigate } from "react-router-dom";

import { createPost } from '../../redux/posts/postActions';
import { createEvidenceThunk } from '../../redux/posts/postEvidence/postEvidenceActions';
import { fetchGlobalSearchResults } from '../../redux/searchBar/searchActions';
import { fetchEdgarFilingsData } from '../../redux/stockData/edgar/edgarActions';
import { fetchSentimentData } from '../../redux/stockSentiment/sentimentActions';
import SuggestionList from '../../components/searchBars/SuggestionList';
import { fireConfettiBurst } from '../../utils/celebration';

/* =========================
   CONSTANTS
========================= */
const MAX_EVIDENCE_ITEMS = 3;
const MAX_TITLE_LENGTH = 200;
const MAX_CONTENT_LENGTH = 10000;
const MAX_ASSOCIATED_STOCKS = 10;
const MIN_TITLE_LENGTH = 10;
const MIN_CONTENT_LENGTH = 50;
const DEBOUNCE_DELAY = 300;
const AUTO_DETECT_DEBOUNCE = 500;
const SUCCESS_MESSAGE_DURATION = 2000;
const AUTO_SAVE_DELAY = 2000;
const DRAFT_STORAGE_KEY = 'post_draft';

const EVIDENCE_TYPES = {
  NEWS: 'news',
  FILING: 'filing',
  LINK: 'link'
};

const BACKEND_EVIDENCE_TYPES = {
  [EVIDENCE_TYPES.NEWS]: 'news_article',
  [EVIDENCE_TYPES.FILING]: 'sec_filing',
  [EVIDENCE_TYPES.LINK]: 'other'
};

const VALIDATION_RULES = {
  title: {
    minLength: MIN_TITLE_LENGTH,
    maxLength: MAX_TITLE_LENGTH,
    required: true
  },
  content: {
    minLength: MIN_CONTENT_LENGTH,
    maxLength: MAX_CONTENT_LENGTH,
    required: false
  },
  evidenceUrl: {
    pattern: /^https?:\/\/.+/i,
    required: true
  },
  evidenceTitle: {
    minLength: 3,
    maxLength: 200,
    required: true
  }
};

/* =========================
   UTILITY FUNCTIONS
========================= */

/**
 * Safely parse an ID to integer
 */
const safeParseId = (id) => {
  if (id === null || id === undefined) return null;
  const parsed = parseInt(id, 10);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Remove duplicate stocks by symbol (case-insensitive)
 */
const uniqueBySymbol = (arr) =>
  arr.filter(
    (s, i) =>
      arr.findIndex(
        x => x.symbol?.toUpperCase() === s.symbol?.toUpperCase()
      ) === i
  );

/**
 * Validate URL format
 */
const isValidUrl = (url) => {
  try {
    new URL(url);
    return VALIDATION_RULES.evidenceUrl.pattern.test(url);
  } catch {
    return false;
  }
};

/**
 * Sanitize user input (prevent XSS while preserving spaces and basic punctuation)
 */
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trimStart(); // Only trim start to preserve intentional trailing spaces
};

/**
 * Extract stock symbols from text
 */
const extractStockSymbols = (text) => {
  if (!text) return [];
  const symbolRegex = /\$([A-Za-z]{1,5})(?=\b|[^A-Za-z0-9])/g;
  const matches = [...text.matchAll(symbolRegex)];
  return [...new Set(matches.map(m => m[1].toUpperCase()))];
};

/**
 * Validate form data
 */
const validatePostData = (postData, t) => {
  const errors = [];

  // Title validation
  const trimmedTitle = postData.title?.trim() || '';
  if (!trimmedTitle) {
    errors.push({ field: 'title', message: t('stockers.postForm.validation.titleRequired'), severity: 'error' });
  } else if (trimmedTitle.length < VALIDATION_RULES.title.minLength) {
    errors.push({
      field: 'title',
      message: t('stockers.postForm.validation.titleNeedsMoreChars', { count: VALIDATION_RULES.title.minLength - trimmedTitle.length }),
      severity: 'warning'
    });
  } else if (trimmedTitle.length > VALIDATION_RULES.title.maxLength) {
    errors.push({
      field: 'title',
      message: t('stockers.postForm.validation.titleExceedsMax', { count: trimmedTitle.length - VALIDATION_RULES.title.maxLength }),
      severity: 'error'
    });
  }

  // Content validation (optional but recommended)
  const trimmedContent = postData.content?.trim() || '';
  if (trimmedContent && trimmedContent.length > VALIDATION_RULES.content.maxLength) {
    errors.push({
      field: 'content',
      message: t('stockers.postForm.validation.contentExceedsMax', { count: trimmedContent.length - VALIDATION_RULES.content.maxLength }),
      severity: 'error'
    });
  }

  // Content recommendation
  if (trimmedContent && trimmedContent.length < VALIDATION_RULES.content.minLength) {
    errors.push({
      field: 'content',
      message: t('stockers.postForm.validation.considerAddingMore', { count: VALIDATION_RULES.content.minLength - trimmedContent.length }),
      severity: 'info'
    });
  }

  // Stock limit validation
  if (postData.associatedStocks && postData.associatedStocks.length > MAX_ASSOCIATED_STOCKS) {
    errors.push({
      field: 'stocks',
      message: t('stockers.postForm.validation.maxStocksAllowed', { max: MAX_ASSOCIATED_STOCKS }),
      severity: 'error'
    });
  }

  return errors;
};

/**
 * Validate evidence item
 */
const validateEvidence = (evidence, t) => {
  const errors = [];

  if (!evidence.url || !isValidUrl(evidence.url)) {
    errors.push({ field: 'url', message: t('stockers.postForm.validation.urlRequired'), severity: 'error' });
  }

  const trimmedTitle = evidence.title?.trim() || '';
  if (!trimmedTitle) {
    errors.push({ field: 'title', message: t('stockers.postForm.validation.evidenceTitleRequired'), severity: 'error' });
  } else if (trimmedTitle.length < VALIDATION_RULES.evidenceTitle.minLength) {
    errors.push({
      field: 'title',
      message: t('stockers.postForm.validation.evidenceTitleMinLength', { min: VALIDATION_RULES.evidenceTitle.minLength }),
      severity: 'error'
    });
  } else if (trimmedTitle.length > VALIDATION_RULES.evidenceTitle.maxLength) {
    errors.push({
      field: 'title',
      message: t('stockers.postForm.validation.evidenceTitleMaxLength', { max: VALIDATION_RULES.evidenceTitle.maxLength }),
      severity: 'error'
    });
  }

  return errors;
};

/**
 * Load draft from localStorage
 */
const loadDraft = () => {
  try {
    const draft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (draft) {
      const parsed = JSON.parse(draft);
      // Validate draft age (discard if older than 7 days)
      if (parsed.timestamp && Date.now() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
        return parsed.data;
      }
    }
  } catch (error) {
    console.error('Error loading draft:', error);
  }
  return null;
};

/**
 * Save draft to localStorage
 */
const saveDraft = (data) => {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error saving draft:', error);
  }
};

/**
 * Clear draft from localStorage
 */
const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing draft:', error);
  }
};

/* =========================
   SUB-COMPONENTS
========================= */

/**
 * Enhanced Alert Component with severity levels
 */
const Alert = ({ errors, onDismiss, className = '' }) => {
  const { t } = useTranslation();
  if (!errors || errors.length === 0) return null;

  const errorsByType = errors.reduce((acc, err) => {
    const severity = err.severity || 'error';
    if (!acc[severity]) acc[severity] = [];
    acc[severity].push(err);
    return acc;
  }, {});

  const severityConfig = {
    error: {
      bg: 'bg-red-900/40',
      border: 'border-red-500/50',
      icon: AlertCircle,
      iconColor: 'text-red-400',
      textColor: 'text-red-300',
      title: t('stockers.postForm.alert.error'),
      titlePlural: t('stockers.postForm.alert.errors')
    },
    warning: {
      bg: 'bg-yellow-900/40',
      border: 'border-yellow-500/50',
      icon: Info,
      iconColor: 'text-yellow-400',
      textColor: 'text-yellow-300',
      title: t('stockers.postForm.alert.suggestion'),
      titlePlural: t('stockers.postForm.alert.suggestions')
    },
    info: {
      bg: 'bg-blue-900/40',
      border: 'border-blue-500/50',
      icon: Info,
      iconColor: 'text-blue-400',
      textColor: 'text-blue-300',
      title: t('stockers.postForm.alert.tip'),
      titlePlural: t('stockers.postForm.alert.tips')
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {Object.entries(errorsByType).map(([severity, items]) => {
        const config = severityConfig[severity];
        const Icon = config.icon;

        return (
          <div
            key={severity}
            className={`${config.bg} border ${config.border} rounded-xl p-4`}
            role="alert"
          >
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${config.textColor} mb-2`}>
                  {items.length === 1 ? config.title : `${items.length} ${config.titlePlural}`}
                </h4>
                <ul className="space-y-1">
                  {items.map((error, idx) => (
                    <li key={idx} className={`text-sm ${config.textColor}`}>
                      • {error.message || error}
                    </li>
                  ))}
                </ul>
              </div>
              {onDismiss && severity !== 'error' && (
                <button
                  onClick={onDismiss}
                  className={`flex-shrink-0 p-1 hover:bg-${severity === 'warning' ? 'yellow' : 'blue'}-800/50 rounded transition-colors`}
                  aria-label={t('stockers.postForm.alert.dismiss')}
                >
                  <X className={`w-4 h-4 ${config.textColor}`} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Enhanced Character Counter with progress bar
 */
const CharacterCounter = ({ current, max, label, showProgressBar = true }) => {
  const percentage = (current / max) * 100;
  const isNearLimit = percentage > 80;
  const isAtLimit = percentage >= 100;
  
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs font-medium ${
        isAtLimit ? 'text-red-400' : 
        isNearLimit ? 'text-yellow-400' : 
        'text-gray-400'
      }`}>
        {label}: {current.toLocaleString()}/{max.toLocaleString()}
      </span>
      {showProgressBar && isNearLimit && (
        <div className="flex-1 max-w-24 h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${
              isAtLimit ? 'bg-red-500' : 'bg-yellow-500'
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin="0"
            aria-valuemax="100"
          />
        </div>
      )}
    </div>
  );
};

/**
 * Evidence Item Display Component
 */
const EvidenceItem = ({ item, onRemove, index }) => {
  const { t } = useTranslation();
  const getEvidenceIcon = (type) => {
    switch(type) {
      case EVIDENCE_TYPES.NEWS: return <Newspaper className="w-4 h-4" />;
      case EVIDENCE_TYPES.FILING: return <FileText className="w-4 h-4" />;
      case EVIDENCE_TYPES.LINK: return <LinkIcon className="w-4 h-4" />;
      default: return <LinkIcon className="w-4 h-4" />;
    }
  };
  
  const getEvidenceColor = (type) => {
    switch(type) {
      case EVIDENCE_TYPES.NEWS: 
        return 'from-blue-600/20 to-blue-500/10 border-blue-500/30';
      case EVIDENCE_TYPES.FILING: 
        return 'from-purple-600/20 to-purple-500/10 border-purple-500/30';
      case EVIDENCE_TYPES.LINK: 
        return 'from-gray-600/20 to-gray-500/10 border-gray-500/30';
      default: 
        return 'from-gray-600/20 to-gray-500/10 border-gray-500/30';
    }
  };
  
  return (
    <div 
      className={`flex items-start gap-3 p-3 bg-gradient-to-r ${getEvidenceColor(item.type)} border rounded-lg transition-all hover:shadow-lg group`}
      role="article"
      aria-label={t('stockers.postForm.evidenceItemAria.item', { index: index + 1, title: item.title })}
    >
      <div className="flex-shrink-0 mt-1">
        {getEvidenceIcon(item.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate" title={item.title}>
              {item.title}
            </p>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-400 hover:text-blue-300 truncate block transition-colors"
              title={item.url}
            >
              {item.url}
            </a>
            {item.relevance_note && (
              <p className="text-xs text-gray-300 mt-1 line-clamp-2">
                {item.relevance_note}
              </p>
            )}
          </div>
          <button
            onClick={() => onRemove(item.id)}
            className="flex-shrink-0 p-1 hover:bg-red-500/20 rounded transition-colors opacity-0 group-hover:opacity-100"
            aria-label={t('stockers.postForm.evidenceItemAria.remove', { title: item.title })}
          >
            <X size={14} className="text-gray-400 hover:text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Stock Tag Component
 */
const StockTag = ({ stock, onRemove, disabled = false }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center gap-2 bg-purple-800/70 text-white text-xs px-3 py-1.5 rounded-full transition-all ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700/70'
      }`}
      role="status"
      aria-label={t('stockers.postForm.stockTagAria.tagged', { symbol: stock.symbol })}
    >
      <span className="font-semibold">${stock.symbol}</span>
      {!disabled && (
        <button
          onClick={() => onRemove(stock)}
          className="hover:text-red-300 transition-colors"
          aria-label={t('stockers.postForm.stockTagAria.remove', { symbol: stock.symbol })}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
};

/**
 * Draft Indicator
 */
const DraftIndicator = ({ lastSaved, onRestore, onDiscard }) => {
  const { t } = useTranslation();
  if (!lastSaved) return null;

  return (
    <div className="flex items-center justify-between p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
      <div className="flex items-center gap-2">
        <Save className="w-4 h-4 text-blue-400" />
        <span className="text-xs text-blue-300">
          {t('stockers.postForm.draft.saved', { time: new Date(lastSaved).toLocaleTimeString() })}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onRestore}
          className="text-xs text-blue-400 hover:text-blue-300 underline transition-colors"
        >
          {t('stockers.postForm.draft.restore')}
        </button>
        <button
          onClick={onDiscard}
          className="text-xs text-gray-400 hover:text-red-400 underline transition-colors"
        >
          {t('stockers.postForm.draft.discard')}
        </button>
      </div>
    </div>
  );
};

/* =========================
   MAIN COMPONENT
========================= */
const PostForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux selectors
  // state.posts only exists when featureFlags.posts is enabled (see
  // redux/indexRedux.js) -- the /post-create route itself is always mounted
  // regardless of that flag (only the nav link to it is hidden), so this must
  // not destructure state.posts directly or it crashes anyone who reaches
  // this route directly (bookmark, typed URL, browser back button) while the
  // flag is off.
  const {
    loading: createPostLoading,
    success: createPostSuccess,
    error: createPostError
  } = useSelector(state => state.posts || {});
  
  const stocks = useSelector((state) => state.search.stocks || []);
  const users = useSelector((state) => state.search.users || []);
  const edgarState = useSelector(state => state.edgar || {});
  const sentimentState = useSelector(state => state.sentiment || {});
  
  const filings = useMemo(() => edgarState.filings || [], [edgarState.filings]);
  const newsArticles = useMemo(
    () => sentimentState.sentimentData?.articles || [], 
    [sentimentState.sentimentData]
  );
  
  // URL params
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get("symbol");
  
  // Form state
  const [localPostData, setLocalPostData] = useState({
    title: '',
    content: '',
    associatedStocks: [],
    targetPct: '',
    horizonDays: ''
  });
  const [showTargetFields, setShowTargetFields] = useState(false);
  // MAR Art 20 position disclosure -- StockerPostComposer.jsx/
  // GroupPostComposer.jsx both have this checkbox; this page (the main
  // /post-create form) never did, so every post created here silently
  // sent the schema default (false) even though Post.jsx actively renders
  // a disclosure badge expecting this to sometimes be true.
  const [disclosesPosition, setDisclosesPosition] = useState(false);

  const [wordCount, setWordCount] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  
  // Evidence state
  const [evidenceItems, setEvidenceItems] = useState([]);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [showEvidencePicker, setShowEvidencePicker] = useState(false);
  const [currentEvidence, setCurrentEvidence] = useState({
    type: EVIDENCE_TYPES.NEWS,
    url: '',
    title: '',
    summary: '',
    relevance_note: ''
  });
  
  // Stock search state
  const [stockQuery, setStockQuery] = useState('');
  const [stockSuggestions, setStockSuggestions] = useState([]);
  const [hoveredSuggestion, setHoveredSuggestion] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showOptimisticSuccess, setShowOptimisticSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [evidenceValidationErrors, setEvidenceValidationErrors] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastDraftSave, setLastDraftSave] = useState(null);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [touchedFields, setTouchedFields] = useState({
    title: false,
    content: false
  });
  
  // Refs
  const searchBarRef = useRef(null);
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  const formRef = useRef(null);
  
  /* =========================
     COMPUTED VALUES
  ========================= */
  const canAddEvidence = evidenceItems.length < MAX_EVIDENCE_ITEMS;
  const hasStocks = localPostData.associatedStocks.length > 0;
  const hasAvailableData = filings.length > 0 || newsArticles.length > 0;
  
  const isFormValid = useMemo(() => {
    const trimmedTitle = localPostData.title?.trim() || '';
    const hasMinTitle = trimmedTitle.length >= MIN_TITLE_LENGTH;
    const hasMaxTitle = trimmedTitle.length <= MAX_TITLE_LENGTH;
    const hasMaxContent = (localPostData.content?.length || 0) <= MAX_CONTENT_LENGTH;
    const hasValidStockCount = localPostData.associatedStocks.length <= MAX_ASSOCIATED_STOCKS;
    const hasNoBlockingErrors = !validationErrors.some(e => e.severity === 'error');
    
    return hasMinTitle && hasMaxTitle && hasMaxContent && hasValidStockCount && hasNoBlockingErrors;
  }, [localPostData.title, localPostData.content, localPostData.associatedStocks, validationErrors]);
  
  /* =========================
     DRAFT MANAGEMENT
  ========================= */
  
  // Load draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft && !symbolParam) {
      setShowDraftPrompt(true);
      setLastDraftSave(Date.now());
    }
  }, []);
  
  const handleRestoreDraft = () => {
    const draft = loadDraft();
    if (draft) {
      setLocalPostData(draft.postData || { title: '', content: '', associatedStocks: [], targetPct: '', horizonDays: '' });
      setEvidenceItems(draft.evidenceItems || []);
      setShowDraftPrompt(false);
      // Mark fields as touched if they have content
      setTouchedFields({
        title: Boolean(draft.postData?.title),
        content: Boolean(draft.postData?.content)
      });
    }
  };
  
  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftPrompt(false);
    setLastDraftSave(null);
  };
  
  // Auto-save draft
  useEffect(() => {
    if (!localPostData.title && !localPostData.content && evidenceItems.length === 0) {
      return; // Don't save empty drafts
    }
    
    const saveDraftDebounced = debounce(() => {
      saveDraft({
        postData: localPostData,
        evidenceItems
      });
      setLastDraftSave(Date.now());
      setHasUnsavedChanges(false);
    }, AUTO_SAVE_DELAY);
    
    setHasUnsavedChanges(true);
    saveDraftDebounced();
    
    return () => saveDraftDebounced.cancel();
  }, [localPostData, evidenceItems]);
  
  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !showSuccessMessage) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, showSuccessMessage]);
  
  /* =========================
     WORD COUNT
  ========================= */
  useEffect(() => {
    const words = localPostData.content?.trim().split(/\s+/) || [];
    setWordCount(words.length === 1 && words[0] === '' ? 0 : words.length);
  }, [localPostData.content]);
  
  /* =========================
     INITIAL SYMBOL FROM URL
  ========================= */
  useEffect(() => {
    if (!symbolParam) return;

    const symbol = symbolParam.toUpperCase();
    const alreadyAdded = localPostData.associatedStocks.some(
      s => s.symbol?.toUpperCase() === symbol
    );

    if (alreadyAdded) return;

    dispatch(fetchGlobalSearchResults(symbol)).then(res => {
      const match = res?.payload?.stocks?.find(
        s => s.symbol?.toUpperCase() === symbol
      );

      if (!match) return;

      const parsedId = safeParseId(match.id);
      if (!parsedId) return;

      setLocalPostData(prev => ({
        ...prev,
        associatedStocks: uniqueBySymbol([
          ...prev.associatedStocks,
          {
            id: parsedId,
            symbol: match.symbol.toUpperCase(),
            name: match.description || match.symbol,
          }
        ])
      }));

      dispatch(fetchEdgarFilingsData(symbol));
      dispatch(fetchSentimentData(symbol));
    });
  }, [symbolParam, dispatch]);
  
  /* =========================
     AUTO-DETECT STOCK SYMBOLS
  ========================= */
  useEffect(() => {
    const detectStockSymbols = () => {
      const detectedSymbols = extractStockSymbols(localPostData.content);

      detectedSymbols.forEach(symbol => {
        const alreadyAdded = localPostData.associatedStocks.some(
          s => s.symbol?.toUpperCase() === symbol
        );

        if (alreadyAdded) return;
        
        if (localPostData.associatedStocks.length >= MAX_ASSOCIATED_STOCKS) return;

        dispatch(fetchGlobalSearchResults(symbol)).then(result => {
          const matching = result?.payload?.stocks?.find(
            s => s.symbol?.toUpperCase() === symbol
          );
          if (!matching) return;

          const parsedId = safeParseId(matching.id);
          if (parsedId == null) return;

          setLocalPostData(prev => {
            if (prev.associatedStocks.length >= MAX_ASSOCIATED_STOCKS) return prev;
            
            return {
              ...prev,
              associatedStocks: uniqueBySymbol([
                ...prev.associatedStocks,
                {
                  id: parsedId,
                  symbol: matching.symbol.toUpperCase(),
                  name: matching.description || matching.symbol
                }
              ])
            };
          });

          dispatch(fetchEdgarFilingsData(symbol));
          dispatch(fetchSentimentData(symbol));
        });
      });
    };

    const debounced = debounce(detectStockSymbols, AUTO_DETECT_DEBOUNCE);
    if (localPostData.content) debounced();
    return () => debounced.cancel();
  }, [localPostData.content, dispatch, localPostData.associatedStocks]);
  
  /* =========================
     FETCH DATA FOR NEW STOCKS
  ========================= */
  useEffect(() => {
    const latest = localPostData.associatedStocks.at(-1);
    if (!latest?.symbol) return;

    dispatch(fetchEdgarFilingsData(latest.symbol));
    dispatch(fetchSentimentData(latest.symbol));
  }, [localPostData.associatedStocks, dispatch]);
  
  /* =========================
     STOCK SEARCH DEBOUNCE
  ========================= */
  const debouncedFetchSuggestions = useCallback(
    debounce((input) => {
      if (input.trim() !== '') {
        dispatch(fetchGlobalSearchResults(input)); 
      }
    }, DEBOUNCE_DELAY),
    [dispatch]
  );
  
  useEffect(() => {
    if (Array.isArray(stocks)) {
      const transformedStocks = stocks.slice(0, 5).map(stock => ({
        ...stock,
        type: 'stock',
        displayName: `${stock.symbol} - ${stock.description?.substring(0, 50)}...` || stock.symbol,
        name: stock.description || stock.symbol
      }));
      setStockSuggestions(transformedStocks);
    } else {
      setStockSuggestions([]);
    }
  }, [stocks]);

  useEffect(() => {
    debouncedFetchSuggestions(stockQuery);
  }, [stockQuery, debouncedFetchSuggestions]);
  
  /* =========================
     CLICK OUTSIDE HANDLER
  ========================= */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        setStockSuggestions([]);
        setHoveredSuggestion('');
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  /* =========================
     SUBMISSION STATE MANAGEMENT
  ========================= */
  useEffect(() => {
    if (isSubmitting) {
      if (createPostSuccess && showOptimisticSuccess) return;
      if (createPostSuccess) {
        setIsSubmitting(false);
        clearDraft(); // Clear draft on successful submission
        
        if (!showOptimisticSuccess) {
          setShowSuccessMessage(true);
          
          setTimeout(() => {
            setLocalPostData({
              title: '',
              content: '',
              associatedStocks: [],
              targetPct: '',
              horizonDays: ''
            });
            setEvidenceItems([]);
            setDisclosesPosition(false);
            setShowSuccessMessage(false);
            setValidationErrors([]);
            setHasUnsavedChanges(false);
            setTouchedFields({ title: false, content: false }); // Reset touched fields
          }, SUCCESS_MESSAGE_DURATION);
        }
      } else if (createPostError) {
        setShowOptimisticSuccess(false);
        setIsSubmitting(false);
      }
    }
  }, [createPostSuccess, createPostError, isSubmitting, showOptimisticSuccess, navigate]);
   
  useEffect(() => {
    if (showOptimisticSuccess) {
      const clearFormTimeout = setTimeout(() => {
        setLocalPostData({
          title: '',
          content: '',
          associatedStocks: [],
          targetPct: '',
          horizonDays: ''
        });
        setEvidenceItems([]);
        setDisclosesPosition(false);
        setShowOptimisticSuccess(false);
        setValidationErrors([]);
        setHasUnsavedChanges(false);
        setTouchedFields({ title: false, content: false }); // Reset touched fields
      }, SUCCESS_MESSAGE_DURATION);
      
      return () => clearTimeout(clearFormTimeout);
    }
  }, [showOptimisticSuccess]);

  // Confetti exactly once per successful publish, regardless of which of
  // the two success flags (optimistic vs. server-confirmed) flips first.
  const hasCelebratedRef = useRef(false);
  useEffect(() => {
    const isSuccess = showSuccessMessage || showOptimisticSuccess;
    if (isSuccess && !hasCelebratedRef.current) {
      hasCelebratedRef.current = true;
      fireConfettiBurst();
    } else if (!isSuccess) {
      hasCelebratedRef.current = false;
    }
  }, [showSuccessMessage, showOptimisticSuccess]);

  /* =========================
     VALIDATION
  ========================= */
  useEffect(() => {
    // Only show validation errors for fields the user has touched
    if (!touchedFields.title && !touchedFields.content) {
      setValidationErrors([]);
      return;
    }
    
    const errors = validatePostData(localPostData, t);

    // Filter errors based on touched fields
    const relevantErrors = errors.filter(error => {
      if (error.field === 'title' && !touchedFields.title) return false;
      if (error.field === 'content' && !touchedFields.content) return false;
      return true;
    });

    setValidationErrors(relevantErrors);
  }, [localPostData, touchedFields, t]);
  
  /* =========================
     FORM HANDLERS
  ========================= */
  const handleContentChange = (e) => {
    const value = e.target.value;
    // Mark as touched on first interaction
    if (!touchedFields.content && value.length > 0) {
      setTouchedFields(prev => ({ ...prev, content: true }));
    }
    // Don't sanitize during typing to preserve spaces and normal text entry
    if (value.length <= MAX_CONTENT_LENGTH) {
      setLocalPostData(prev => ({ ...prev, content: value }));
    }
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    // Mark as touched on first interaction
    if (!touchedFields.title && value.length > 0) {
      setTouchedFields(prev => ({ ...prev, title: true }));
    }
    // Don't sanitize during typing to preserve spaces and normal text entry
    if (value.length <= MAX_TITLE_LENGTH) {
      setLocalPostData(prev => ({ ...prev, title: value }));
    }
  };

  const togglePreviewMode = () => {
    setPreviewMode(!previewMode);
  };

  const handleCreatePost = async () => {
    // Mark all fields as touched when user attempts to submit
    setTouchedFields({ title: true, content: true });
    
    // Sanitize before submission
    const sanitizedTitle = sanitizeInput(localPostData.title);
    const sanitizedContent = sanitizeInput(localPostData.content);
    
    // Final validation with sanitized data
    const postDataToValidate = {
      ...localPostData,
      title: sanitizedTitle,
      content: sanitizedContent
    };
    
    const errors = validatePostData(postDataToValidate, t);
    const blockingErrors = errors.filter(e => e.severity === 'error');
    
    if (blockingErrors.length > 0) {
      setValidationErrors(errors);
      if (titleRef.current && blockingErrors.some(e => e.field === 'title')) {
        titleRef.current.focus();
      }
      return;
    }
    
    const validStocks = localPostData.associatedStocks.filter(stock => {
      const parsedId = safeParseId(stock.id);
      return parsedId !== null && Number.isInteger(parsedId);
    });
    
    const stockIds = validStocks.map(s => safeParseId(s.id)).filter(Boolean);

    const parsedTargetPct = parseFloat(localPostData.targetPct);
    const parsedHorizonDays = parseInt(localPostData.horizonDays, 10);

    const postDataForBackend = {
      title: sanitizedTitle.trim(),
      content: sanitizedContent.trim(),
      stock_ids: stockIds.length ? stockIds : undefined,
      user_target_pct: Number.isFinite(parsedTargetPct) ? parsedTargetPct : undefined,
      user_horizon_days: Number.isFinite(parsedHorizonDays) ? parsedHorizonDays : undefined,
      discloses_position: disclosesPosition
    };
    
    setIsSubmitting(true);
    setShowOptimisticSuccess(true);
    setValidationErrors([]);
    
    try {
      const postResult = await dispatch(createPost({ newPostData: postDataForBackend })).unwrap();
      
      // Create evidence items if any
      if (evidenceItems.length > 0 && postResult?.id) {
        const evidencePromises = evidenceItems.map(evidence => 
          dispatch(createEvidenceThunk({
            post_id: postResult.id,
            evidence_type: BACKEND_EVIDENCE_TYPES[evidence.type],
            source_url: evidence.url,
            title: evidence.title,
            excerpt: evidence.summary,
            relevance_note: evidence.relevance_note
          }))
        );
        
        await Promise.all(evidencePromises);
      }
      
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error creating post with evidence:", error);
      setValidationErrors([{
        field: 'form',
        message: error.message || t('stockers.postForm.validation.failedToCreatePost'),
        severity: 'error'
      }]);
      setShowOptimisticSuccess(false);
      setIsSubmitting(false);
    }
  };

  /* =========================
     EVIDENCE HANDLERS
  ========================= */
  const quickAddEvidence = (item, type) => {
    if (!canAddEvidence) {
      setValidationErrors([{
        field: 'evidence',
        message: t('stockers.postForm.validation.maxEvidenceAllowed', { max: MAX_EVIDENCE_ITEMS }),
        severity: 'warning'
      }]);
      return;
    }

    const evidence = {
      id: Date.now(),
      type: type,
      url: item.url || item.filing_url || item.source_url || '#',
      title: item.title || item.form || t('stockers.postForm.untitled'),
      summary: item.summary || item.filing_type || '',
      relevance_note: ''
    };

    setEvidenceItems([...evidenceItems, evidence]);
    setShowEvidencePicker(false);
  };

  const addEvidence = () => {
    // Validate evidence
    const errors = validateEvidence(currentEvidence, t);
    if (errors.length > 0) {
      setEvidenceValidationErrors(errors);
      return;
    }

    if (!canAddEvidence) {
      setEvidenceValidationErrors([{
        field: 'evidence',
        message: t('stockers.postForm.validation.maxEvidenceAllowed', { max: MAX_EVIDENCE_ITEMS }),
        severity: 'error'
      }]);
      return;
    }
    
    setEvidenceItems([...evidenceItems, { 
      ...currentEvidence, 
      id: Date.now(),
      url: sanitizeInput(currentEvidence.url),
      title: sanitizeInput(currentEvidence.title),
      summary: sanitizeInput(currentEvidence.summary),
      relevance_note: sanitizeInput(currentEvidence.relevance_note)
    }]);
    
    setCurrentEvidence({
      type: EVIDENCE_TYPES.NEWS,
      url: '',
      title: '',
      summary: '',
      relevance_note: ''
    });
    setShowEvidenceModal(false);
    setEvidenceValidationErrors([]);
  };

  const removeEvidence = (id) => {
    setEvidenceItems(evidenceItems.filter(item => item.id !== id));
  };

  /* =========================
     STOCK SEARCH HANDLERS
  ========================= */
  const handleStockInputChange = (e) => {
    setStockQuery(e.target.value);
    setHoveredSuggestion('');
  };

  const handleStockInputFocus = () => {
    setIsFocused(true);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type !== 'stock') return;
    
    if (localPostData.associatedStocks.length >= MAX_ASSOCIATED_STOCKS) {
      setValidationErrors([{
        field: 'stocks',
        message: t('stockers.postForm.validation.maxStocksAllowed', { max: MAX_ASSOCIATED_STOCKS }),
        severity: 'warning'
      }]);
      return;
    }

    const parsedId = safeParseId(suggestion.id);
    if (parsedId === null || !Number.isInteger(parsedId)) return;

    setLocalPostData(prev => ({
      ...prev,
      associatedStocks: uniqueBySymbol([
        ...prev.associatedStocks,
        {
          id: parsedId,
          symbol: suggestion.symbol.toUpperCase(),
          name: suggestion.description || suggestion.symbol,
        }
      ])
    }));

    setStockQuery('');
    setStockSuggestions([]);
    setHoveredSuggestion('');
    setIsFocused(false);
  };

  const handleSuggestionHover = (suggestion) => {
    setHoveredSuggestion(suggestion.symbol || '');
  };

  const removeStockTag = (stockToRemove) => {
    setLocalPostData(prev => ({
      ...prev,
      associatedStocks: prev.associatedStocks.filter(
        s => s.symbol?.toUpperCase() !== stockToRemove.symbol?.toUpperCase()
      )
    }));
  };

  /* =========================
     FORMATTING HELPERS
  ========================= */
  const formatContentWithStockHighlights = (content) => {
    if (!content) return '';
    
    return content.split(/(\$[A-Za-z]{1,5}\b)/i).map((segment, index) => {
      if (segment.match(/^\$[A-Za-z]{1,5}$/i)) {
        return (
          <span 
            key={index} 
            className="inline-block bg-purple-800 text-white text-sm rounded-full px-2 py-0.5 mx-0.5"
          >
            {segment.toUpperCase()}
          </span>
        );
      }
      return segment;
    });
  };

  /* =========================
     KEYBOARD SHORTCUTS
  ========================= */
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (isFormValid && !isSubmitting) {
          handleCreatePost();
        }
      }
      
      // Escape to close modals
      if (e.key === 'Escape') {
        if (showEvidenceModal) {
          setShowEvidenceModal(false);
        }
        if (showEvidencePicker) {
          setShowEvidencePicker(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFormValid, isSubmitting, showEvidenceModal, showEvidencePicker]);

  /* =========================
     RENDER
  ========================= */
  return (
    <div className="flex-1 bg-gray-900 text-gray-200 pb-20">
      <main className="p-4 sm:p-6">
        <div className="max-w-3xl mx-auto">

          <div 
            ref={formRef}
            className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur border border-blue-500/20 rounded-2xl shadow-2xl overflow-visible relative"
          >

            {/* Success Overlay */}
            {(showSuccessMessage || showOptimisticSuccess) && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-gray-900/95 backdrop-blur-sm z-20 rounded-2xl"
                role="status"
                aria-live="polite"
              >
                <div className="text-center p-6">
                  <CheckCircle size={56} className="text-emerald-400 mx-auto mb-4 animate-pulse" />
                  <h3 className="text-xl font-bold text-white mb-2">{t('stockers.postForm.successTitle')}</h3>
                  <p className="text-sm text-gray-400">
                    {evidenceItems.length > 0
                      ? t('stockers.postForm.successBodyWithEvidence', { count: evidenceItems.length })
                      : t('stockers.postForm.successBodyBase')}
                  </p>
                </div>
              </div>
            )}

            <div className="p-4 sm:p-6 space-y-5">

              {/* Header */}
              <div className="flex items-center gap-3">
                <FaUserCircle size={32} className="text-gray-400" />
                <div className="flex-1">
                  <span className="text-base font-semibold text-gray-200">
                    {t('stockers.postForm.header.title')}
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('stockers.postForm.header.subtitle')}
                  </p>
                </div>
                {lastDraftSave && !showSuccessMessage && (
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    <Save className="w-3 h-3" />
                    <span>{t('stockers.postForm.header.saved')}</span>
                  </div>
                )}
              </div>

              {/* Draft Prompt */}
              {showDraftPrompt && (
                <DraftIndicator
                  lastSaved={lastDraftSave}
                  onRestore={handleRestoreDraft}
                  onDiscard={handleDiscardDraft}
                />
              )}

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <Alert 
                  errors={validationErrors} 
                  onDismiss={() => {
                    // Only dismiss warnings and info, keep errors
                    const remainingErrors = validationErrors.filter(e => e.severity === 'error');
                    setValidationErrors(remainingErrors);
                  }}
                />
              )}

              {/* Title */}
              <div>
                <label htmlFor="post-title" className="sr-only">{t('stockers.postForm.titleField.srLabel')}</label>
                <input
                  id="post-title"
                  ref={titleRef}
                  type="text"
                  placeholder={t('stockers.postForm.titleField.placeholder')}
                  value={localPostData.title}
                  onChange={handleTitleChange}
                  maxLength={MAX_TITLE_LENGTH}
                  className="w-full bg-transparent border-b-2 border-gray-600 text-xl font-semibold text-white placeholder-gray-500 focus:outline-none focus:border-blue-400 transition-colors pb-2"
                  aria-label={t('stockers.postForm.titleField.srLabel')}
                  aria-required="true"
                  aria-invalid={localPostData.title.trim().length > 0 && localPostData.title.trim().length < MIN_TITLE_LENGTH}
                  disabled={isSubmitting}
                />
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                  <CharacterCounter
                    current={localPostData.title.length}
                    max={MAX_TITLE_LENGTH}
                    label={t('stockers.postForm.titleField.counterLabel')}
                    showProgressBar={true}
                  />
                  {localPostData.title.trim().length > 0 && localPostData.title.trim().length < MIN_TITLE_LENGTH && (
                    <span className="text-xs text-blue-400 flex items-center gap-1 bg-blue-900/20 px-2 py-1 rounded-full">
                      <Info size={12} />
                      {t('stockers.postForm.moreCharsForMinimum', { count: MIN_TITLE_LENGTH - localPostData.title.trim().length })}
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              {!previewMode ? (
                <div>
                  <label htmlFor="post-content" className="sr-only">{t('stockers.postForm.contentField.srLabel')}</label>
                  <textarea
                    id="post-content"
                    ref={contentRef}
                    placeholder={t('stockers.postForm.contentField.placeholder')}
                    value={localPostData.content}
                    onChange={handleContentChange}
                    rows="8"
                    maxLength={MAX_CONTENT_LENGTH}
                    className="w-full p-4 rounded-xl bg-gray-800/60 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none transition-all leading-relaxed"
                    aria-label={t('stockers.postForm.contentField.srLabel')}
                    disabled={isSubmitting}
                  />
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <CharacterCounter
                      current={localPostData.content.length}
                      max={MAX_CONTENT_LENGTH}
                      label={t('stockers.postForm.contentField.counterLabel')}
                      showProgressBar={true}
                    />
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>{t('stockers.postForm.wordCount', { count: wordCount })}</span>
                      {wordCount > 0 && wordCount < 20 && (
                        <span className="text-blue-400 flex items-center gap-1 bg-blue-900/20 px-2 py-1 rounded-full">
                          <Info size={12} />
                          {t('stockers.postForm.considerAddingMoreDetail')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-5 bg-gray-800/60 border border-gray-700 rounded-xl">
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {localPostData.title || t('stockers.postForm.untitledPost')}
                  </h2>
                  <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {formatContentWithStockHighlights(localPostData.content)}
                  </div>
                  {localPostData.content.trim().length === 0 && (
                    <p className="text-gray-500 italic">{t('stockers.postForm.noContentYet')}</p>
                  )}
                </div>
              )}

              {/* Preview Toggle */}
              <div className="flex justify-end">
                <button
                  onClick={togglePreviewMode}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-purple-400 transition-colors"
                  aria-label={previewMode ? t('stockers.postForm.editPost') : t('stockers.postForm.previewPost')}
                  disabled={isSubmitting}
                >
                  <FaEye className="w-4 h-4" />
                  {previewMode ? t('stockers.postForm.continueEditing') : t('stockers.postForm.previewPostButton')}
                </button>
              </div>

              {/* Associated Stock Pills */}
              {hasStocks && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span className="font-medium">{t('stockers.postForm.taggedStocks')}</span>
                    <span className="text-xs bg-gray-700/50 px-2 py-0.5 rounded-full">
                      {localPostData.associatedStocks.length}/{MAX_ASSOCIATED_STOCKS}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2" role="list" aria-label={t('stockers.postForm.taggedStocks')}>
                    {localPostData.associatedStocks.map(stock => (
                      <StockTag 
                        key={stock.id} 
                        stock={stock} 
                        onRemove={removeStockTag}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Optional structured prediction target -- when set, this is what
                  actually gets checked at resolution time (does the real move
                  meet/exceed this %?), instead of relying on the AI to guess a
                  target from free-text, which rarely finds one. */}
              {hasStocks && (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setShowTargetFields(!showTargetFields)}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-purple-400 transition-colors"
                    aria-expanded={showTargetFields}
                    disabled={isSubmitting}
                  >
                    {showTargetFields ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    <Target size={16} />
                    <span>{t('stockers.postForm.addPriceTarget')}</span>
                  </button>
                  {showTargetFields && (
                    <div className="p-4 bg-gray-800/60 border border-gray-700 rounded-xl space-y-3">
                      <p className="text-xs text-gray-500">
                        {t('stockers.postForm.priceTargetHint')}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className="flex-1">
                          <span className="block text-xs text-gray-400 mb-1">{t('stockers.postForm.targetMove')}</span>
                          <input
                            type="number"
                            step="0.1"
                            placeholder={t('stockers.postForm.targetMovePlaceholder')}
                            value={localPostData.targetPct}
                            onChange={(e) => setLocalPostData(prev => ({ ...prev, targetPct: e.target.value }))}
                            className="w-full p-2.5 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                            disabled={isSubmitting}
                          />
                        </label>
                        <label className="flex-1">
                          <span className="block text-xs text-gray-400 mb-1">{t('stockers.postForm.byWhen')}</span>
                          <select
                            value={localPostData.horizonDays}
                            onChange={(e) => setLocalPostData(prev => ({ ...prev, horizonDays: e.target.value }))}
                            className="w-full p-2.5 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                            disabled={isSubmitting}
                          >
                            <option value="">{t('stockers.postForm.horizonOptions.default')}</option>
                            <option value="7">{t('stockers.postForm.horizonOptions.week1')}</option>
                            <option value="14">{t('stockers.postForm.horizonOptions.weeks2')}</option>
                            <option value="30">{t('stockers.postForm.horizonOptions.month1')}</option>
                            <option value="90">{t('stockers.postForm.horizonOptions.months3')}</option>
                          </select>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Position disclosure -- MAR Art 20. This page had no way to
                  set this at all; every post created here silently sent the
                  schema default (false) even though Post.jsx renders a
                  disclosure badge expecting it to sometimes be true. Shown
                  whenever there's content to disclose against -- this page
                  has no separate directional-call button to gate behind,
                  unlike the other two composers. */}
              {localPostData.content.trim().length > 0 && (
                <label className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-500/5 border border-amber-500/20 rounded-xl cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={disclosesPosition}
                    onChange={(e) => setDisclosesPosition(e.target.checked)}
                    disabled={isSubmitting}
                    className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0"
                  />
                  <span className="text-xs text-gray-300 leading-relaxed">
                    <span className="font-medium text-white">{t('dashboard.postComposer.disclosure.statement')}</span>{' '}
                    {hasStocks
                      ? t('dashboard.postComposer.disclosure.inTicker', { ticker: localPostData.associatedStocks[0]?.symbol })
                      : t('dashboard.postComposer.disclosure.inAssets')}{' '}
                    {t('dashboard.postComposer.disclosure.suffix')}
                  </span>
                </label>
              )}

              {/* Evidence Items Display */}
              {evidenceItems.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FileText size={16} />
                    <span className="font-medium">{t('stockers.postForm.evidenceAttached')}</span>
                    <span className="text-xs bg-gray-700/50 px-2 py-0.5 rounded-full">
                      {evidenceItems.length}/{MAX_EVIDENCE_ITEMS}
                    </span>
                  </div>
                  <div className="space-y-2" role="list" aria-label={t('stockers.postForm.evidenceItemsAria')}>
                    {evidenceItems.map((item, index) => (
                      <EvidenceItem 
                        key={item.id} 
                        item={item} 
                        onRemove={removeEvidence}
                        index={index}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Evidence Selection Buttons */}
              {canAddEvidence && hasStocks && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setShowEvidencePicker(!showEvidencePicker)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-sm text-blue-300 transition-all flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-expanded={showEvidencePicker}
                    aria-controls="evidence-picker"
                    disabled={isSubmitting}
                  >
                    {showEvidencePicker ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    <span>{t('stockers.postForm.quickSelectFromStockData')}</span>
                    {hasAvailableData && (
                      <span className="text-xs bg-blue-600/40 px-2 py-0.5 rounded-full">
                        {filings.length + newsArticles.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowEvidenceModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700/50 hover:bg-gray-700/70 border border-gray-600/50 rounded-lg text-sm text-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={t('stockers.postForm.addCustomEvidenceAria')}
                    disabled={isSubmitting}
                  >
                    <Plus size={16} />
                    <span>{t('stockers.postForm.addCustomLink')}</span>
                  </button>
                </div>
              )}

              {/* Evidence Picker Dropdown */}
              {showEvidencePicker && canAddEvidence && (
                <div 
                  id="evidence-picker"
                  className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 space-y-4 max-h-96 overflow-y-auto"
                  role="region"
                  aria-label={t('stockers.postForm.evidencePickerAria')}
                >
                  {/* SEC Filings Section */}
                  {filings.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <FileText size={16} className="text-purple-400" />
                        <h4 className="text-sm font-semibold text-white">{t('stockers.postForm.secFilings')}</h4>
                        <span className="text-xs text-gray-500">{t('stockers.postForm.availableCount', { count: filings.length })}</span>
                      </div>
                      <div className="space-y-2">
                        {filings.slice(0, 5).map((filing, idx) => (
                          <button
                            key={idx}
                            onClick={() => quickAddEvidence(filing, EVIDENCE_TYPES.FILING)}
                            className="w-full text-left p-3 bg-purple-600/10 hover:bg-purple-600/20 border border-purple-500/20 rounded-lg transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-xs font-mono font-bold text-purple-300 bg-purple-900/40 px-2 py-0.5 rounded">
                                    {filing.form || t('stockers.postForm.filingFallback')}
                                  </span>
                                  {filing.filing_date && (
                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                      <Calendar size={12} />
                                      {new Date(filing.filing_date).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-300 line-clamp-2">
                                  {filing.summary || filing.filing_type || t('stockers.postForm.filingSummaryFallback')}
                                </p>
                              </div>
                              <Plus size={16} className="text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* News Articles Section */}
                  {newsArticles.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Newspaper size={16} className="text-blue-400" />
                        <h4 className="text-sm font-semibold text-white">{t('stockers.postForm.recentNews')}</h4>
                        <span className="text-xs text-gray-500">{t('stockers.postForm.availableCount', { count: newsArticles.length })}</span>
                      </div>
                      <div className="space-y-2">
                        {newsArticles.slice(0, 5).map((article, idx) => (
                          <button
                            key={idx}
                            onClick={() => quickAddEvidence(article, EVIDENCE_TYPES.NEWS)}
                            className="w-full text-left p-3 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/20 rounded-lg transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white line-clamp-2 mb-1">
                                  {article.title}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                                  {article.source && <span>{article.source}</span>}
                                  {article.time_published && (
                                    <>
                                      <span>•</span>
                                      <span>{new Date(article.time_published).toLocaleDateString()}</span>
                                    </>
                                  )}
                                  {article.overall_sentiment_label && (
                                    <>
                                      <span>•</span>
                                      <span className={`font-medium ${
                                        article.overall_sentiment_label === 'Bullish' ? 'text-emerald-400' :
                                        article.overall_sentiment_label === 'Bearish' ? 'text-red-400' :
                                        'text-gray-400'
                                      }`}>
                                        {article.overall_sentiment_label}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <Plus size={16} className="text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {!hasAvailableData && (
                    <div className="text-center py-8 text-gray-400">
                      <FileText size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('stockers.postForm.noFilingsOrNews')}</p>
                      <p className="text-xs mt-1">{t('stockers.postForm.tagStockToLoadData')}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Add Evidence Button (when no stocks tagged) */}
              {canAddEvidence && !hasStocks && (
                <button
                  onClick={() => setShowEvidenceModal(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700/50 hover:bg-gray-700/70 border border-gray-600/50 rounded-lg text-sm text-gray-300 transition-all w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={t('stockers.postForm.addCustomEvidenceAria')}
                  disabled={isSubmitting}
                >
                  <Plus size={16} />
                  <span>{t('stockers.postForm.addEvidenceFull')}</span>
                </button>
              )}

              {/* Stock Search */}
              <div ref={searchBarRef} className="relative">
                <label htmlFor="stock-search" className="block text-sm font-medium text-gray-400 mb-2">
                  {t('stockers.postForm.tagStocksOptional')}
                </label>
                <input
                  id="stock-search"
                  type="text"
                  placeholder={t('stockers.postForm.stockSearchPlaceholder')}
                  value={stockQuery}
                  onChange={handleStockInputChange}
                  onFocus={handleStockInputFocus}
                  disabled={localPostData.associatedStocks.length >= MAX_ASSOCIATED_STOCKS || isSubmitting}
                  className="w-full bg-gray-800/60 border border-gray-700 px-4 py-2.5 rounded-lg text-sm text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  aria-label={t('stockers.postForm.stockSearchAria')}
                  aria-autocomplete="list"
                  aria-controls="stock-suggestions"
                />

                {isFocused && stockSuggestions.length > 0 && (
                  <div id="stock-suggestions" role="listbox" className="absolute z-10 w-full mt-1">
                    <SuggestionList
                      suggestions={stockSuggestions}
                      onSuggestionClick={handleSuggestionClick}
                      onSuggestionHover={handleSuggestionHover}
                    />
                  </div>
                )}
                
                {localPostData.associatedStocks.length >= MAX_ASSOCIATED_STOCKS && (
                  <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                    <Info size={12} />
                    {t('stockers.postForm.validation.maxStocksReached', { max: MAX_ASSOCIATED_STOCKS })}
                  </p>
                )}
              </div>

              {/* Action Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-5 border-t border-gray-700">

                <div className="text-xs text-gray-500 space-y-1">
                  <p className="flex items-center gap-2">
                    <span>📊 {t('stockers.postForm.autoEvidenceHint')}</span>
                    <span>•</span>
                    <span>{evidenceItems.length}/{MAX_EVIDENCE_ITEMS} {t('stockers.postForm.evidenceSuffix')}</span>
                  </p>
                  <p className="flex items-center gap-1 text-gray-600">
                    <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] font-mono">Ctrl</kbd> +
                    <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] font-mono ml-1">Enter</kbd>
                    <span>{t('stockers.postForm.toPublish')}</span>
                  </p>
                </div>

                <button
                  onClick={handleCreatePost}
                  disabled={isSubmitting || showOptimisticSuccess || !isFormValid}
                  className={`w-full sm:w-auto px-8 py-3 rounded-xl font-semibold text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
                    isSubmitting || !isFormValid
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 hover:shadow-xl hover:scale-105'
                  }`}
                  aria-label={t('stockers.postForm.publishPostAria')}
                  aria-disabled={isSubmitting || !isFormValid}
                >
                  {isSubmitting ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span>{t('stockers.postForm.publishing')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('stockers.postForm.publishPost')}</span>
                      {evidenceItems.length > 0 && (
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                          +{evidenceItems.length}
                        </span>
                      )}
                    </>
                  )}
                </button>

              </div>

              {/* API Error */}
              {createPostError && !showOptimisticSuccess && (
                <Alert
                  errors={[{ 
                    field: 'api', 
                    message: createPostError, 
                    severity: 'error' 
                  }]}
                />
              )}

            </div>
          </div>
        </div>
      </main>

      {/* Evidence Modal */}
      {showEvidenceModal && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10001] p-4"
          role="dialog"
          aria-labelledby="evidence-modal-title"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEvidenceModal(false);
              setEvidenceValidationErrors([]);
            }
          }}
        >
          <div className="bg-gray-800 border border-gray-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 id="evidence-modal-title" className="text-xl font-bold text-white">
                    {t('stockers.postForm.modal.title')}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('stockers.postForm.modal.subtitle')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowEvidenceModal(false);
                    setEvidenceValidationErrors([]);
                  }}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={t('stockers.postForm.modal.closeAria')}
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Evidence Validation Errors */}
              {evidenceValidationErrors.length > 0 && (
                <Alert 
                  errors={evidenceValidationErrors} 
                  onDismiss={() => setEvidenceValidationErrors([])}
                />
              )}

              {/* Evidence Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('stockers.postForm.modal.evidenceType')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {Object.values(EVIDENCE_TYPES).map((type) => (
                    <button
                      key={type}
                      onClick={() => setCurrentEvidence({ ...currentEvidence, type })}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        currentEvidence.type === type
                          ? 'bg-blue-600 text-white shadow-lg ring-2 ring-blue-400/50'
                          : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                      }`}
                      aria-pressed={currentEvidence.type === type}
                    >
                      {type === EVIDENCE_TYPES.NEWS && <Newspaper className="w-4 h-4" />}
                      {type === EVIDENCE_TYPES.FILING && <FileText className="w-4 h-4" />}
                      {type === EVIDENCE_TYPES.LINK && <LinkIcon className="w-4 h-4" />}
                      <span className="capitalize">{t(`stockers.postForm.modal.types.${type}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* URL Input */}
              <div>
                <label htmlFor="evidence-url" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('stockers.postForm.modal.sourceUrl')} <span className="text-red-400">*</span>
                </label>
                <input
                  id="evidence-url"
                  type="url"
                  placeholder={t('stockers.postForm.modal.urlPlaceholder')}
                  value={currentEvidence.url}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, url: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  aria-required="true"
                />
                <p className="text-xs text-gray-500 mt-1">{t('stockers.postForm.modal.urlHint')}</p>
              </div>

              {/* Title Input */}
              <div>
                <label htmlFor="evidence-title" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('stockers.postForm.modal.titleLabel')} <span className="text-red-400">*</span>
                </label>
                <input
                  id="evidence-title"
                  type="text"
                  placeholder={t('stockers.postForm.modal.titlePlaceholder')}
                  value={currentEvidence.title}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, title: e.target.value })}
                  maxLength={VALIDATION_RULES.evidenceTitle.maxLength}
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  aria-required="true"
                />
                <div className="mt-1">
                  <CharacterCounter
                    current={currentEvidence.title.length}
                    max={VALIDATION_RULES.evidenceTitle.maxLength}
                    label={t('stockers.postForm.modal.titleLabel')}
                    showProgressBar={false}
                  />
                </div>
              </div>

              {/* Summary Input */}
              <div>
                <label htmlFor="evidence-summary" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('stockers.postForm.modal.summaryLabel')}
                </label>
                <textarea
                  id="evidence-summary"
                  placeholder={t('stockers.postForm.modal.summaryPlaceholder')}
                  value={currentEvidence.summary}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, summary: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none transition-all"
                />
              </div>

              {/* Relevance Note */}
              <div>
                <label htmlFor="evidence-relevance" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('stockers.postForm.modal.relevanceLabel')}
                </label>
                <textarea
                  id="evidence-relevance"
                  placeholder={t('stockers.postForm.modal.relevancePlaceholder')}
                  value={currentEvidence.relevance_note}
                  onChange={(e) => setCurrentEvidence({ ...currentEvidence, relevance_note: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2.5 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none transition-all"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowEvidenceModal(false);
                    setEvidenceValidationErrors([]);
                  }}
                  className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t('stockers.postForm.modal.cancel')}
                </button>
                <button
                  onClick={addEvidence}
                  disabled={!currentEvidence.url.trim() || !currentEvidence.title.trim()}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  aria-disabled={!currentEvidence.url.trim() || !currentEvidence.title.trim()}
                >
                  <Plus size={16} />
                  <span>{t('stockers.postForm.modal.addEvidence')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostForm;